import { useState, useEffect, useRef } from 'react';
import adminService from '../../../services/adminService';

const VideoRequestsSection = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const videoInputRef = useRef(null);
  const uploadRequestRef = useRef(null);

  const fetchVideoRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminService.getVideoRequests();
      setRequests(response.requests || response.data || response.videoRequests || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load video requests');
      console.error('Error fetching video requests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideoRequests();
  }, []);

  const handleUpdateStatus = async (requestId, newStatus) => {
    setUpdatingId(requestId);
    try {
      await adminService.updateVideoRequest(requestId, newStatus);
      // Update local state
      setRequests(prev =>
        prev.map(req =>
          req._id === requestId ? { ...req, status: newStatus } : req
        )
      );
    } catch (err) {
      console.error('Error updating video request:', err);
      alert(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleUploadClick = (request) => {
    uploadRequestRef.current = request;
    videoInputRef.current?.click();
  };

  const handleVideoFileChange = async (e) => {
    const file = e.target.files[0];
    const request = uploadRequestRef.current;
    const playerId = request?.player?._id;
    if (!file || !playerId) return;

    setUploadingId(request._id);
    try {
      await adminService.uploadUserVideo(playerId, file);
      // Auto-approve after successful upload
      await adminService.updateVideoRequest(request._id, 'approved');
      setRequests(prev =>
        prev.map(req =>
          req._id === request._id ? { ...req, status: 'approved' } : req
        )
      );
    } catch (err) {
      console.error('Error uploading video:', err);
      alert(err.response?.data?.message || 'Failed to upload video');
    } finally {
      setUploadingId(null);
      uploadRequestRef.current = null;
      e.target.value = "";
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-500/20 text-amber-600 dark:text-yellow-400 border-yellow-500/50',
      approved: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/50',
      rejected: 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/50',
    };
    return statusStyles[status] || statusStyles.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="bg-jp-section rounded-xl border border-jp-line p-6">
        <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest mb-4">Video Requests</h2>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-jp-section rounded-xl border border-jp-line p-6">
        <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest mb-4">Video Requests</h2>
        <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-jp-section rounded-xl border border-jp-line p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest">Video Requests</h2>
        <button
          onClick={fetchVideoRequests}
          className="px-3 py-1.5 text-xs bg-jp-section hover:bg-jp-line text-jp-ink rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-8 text-jp-muted">
          <svg className="w-12 h-12 mx-auto mb-3 text-jp-muted/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <p className="text-sm">No video requests yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-jp-section z-10">
              <tr className="text-center text-jp-muted border-b border-jp-line">
                <th className="pb-3 font-medium">Requested By</th>
                <th className="pb-3 font-medium">For Player</th>
                <th className="pb-3 font-medium">Note</th>
                <th className="pb-3 font-medium">Date</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-jp-line">
              {requests.map((request) => (
                <tr key={request._id} className="text-jp-ink text-center">
                  <td className="py-3">
                    <div>
                      <p className="font-medium">
                        {request.requestedBy?.firstName} {request.requestedBy?.lastName}
                      </p>
                      <p className="text-xs text-jp-muted">{request.requestedBy?.email}</p>
                    </div>
                  </td>
                  <td className="py-3">
                    <div>
                      <p className="font-medium">
                        {request.player?.firstName} {request.player?.lastName}
                      </p>
                      <p className="text-xs text-jp-muted">{request.player?.team?.name || '-'}</p>
                    </div>
                  </td>
                  <td className="py-3">
                    <p className="text-jp-muted max-w-xs truncate" title={request.note}>
                      {request.note || '-'}
                    </p>
                  </td>
                  <td className="py-3 text-jp-muted text-xs">
                    {formatDate(request.createdAt)}
                  </td>
                  <td className="py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(request.status)}`}>
                      {request.status?.charAt(0).toUpperCase() + request.status?.slice(1) || 'Pending'}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center justify-center gap-2">
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleUpdateStatus(request._id, 'approved')}
                            disabled={updatingId === request._id}
                            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1"
                          >
                            {updatingId === request._id ? (
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              'Approve'
                            )}
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(request._id, 'rejected')}
                            disabled={updatingId === request._id}
                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleUploadClick(request)}
                        disabled={uploadingId === request._id}
                        className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 disabled:bg-cyan-800 disabled:cursor-not-allowed text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1"
                      >
                        {uploadingId === request._id ? (
                          <>
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            Upload Video
                          </>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Hidden video file input */}
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoFileChange}
        className="hidden"
      />
    </div>
  );
};

export default VideoRequestsSection;
